// 外国人情報のファイル取り込みモーダル (一覧の「取り込み」ボタン)。
// 手順 = テンプレート配布 → ファイル選択 → 検証 → 取り込む外国人を選択 → 一括登録画面へ遷移。
// 対象を一覧から選ばせる手順・遷移先・保存処理は irohana visa 一括同期 (BulkSyncLearnerModal) と共通にする。

import { FC, useMemo, useState } from "react";
import { Box, CircularProgress, Stack, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { Modal } from "@/components/elements/Modal";
import { Button } from "@/components/elements/Button";
import { DropZone } from "@/components/elements/DropZone";
import { IconImportFile } from "@/assets/icons";
import { AppRoutes } from "@/routes/config";
import { UseVisibleReturn } from "@/hooks/useVisible";
import { TExtendFile } from "@/types/file";
import { background, text } from "@/theme/colors";
import { rounded } from "@/theme/variables";
import { showError } from "@/utils/error";
import { useInitSetup } from "@/providers/InitSetupProvider";
import { USER_TYPE } from "@/types/enum";
import { useEnum } from "@/api/services/global/enum";
import { useGetNationalityList } from "@/api/services/global/master";
import { useGetListCompanyByRso } from "@/api/services/main/company";
import { useLearnerIndustryOptions } from "@/features/learner/hook/useLearnerIndustryOptions";
import { MAX_IMPORT_LEARNERS, TBulkImportRow } from "@/features/learner/components/learner-bulk-import/bulk-import-mock";
import { readLearnerImportFile } from "@/features/learner/components/learner-file-import/learner-import-file-reader";
import { buildImportRowsFromExcel } from "@/features/learner/components/learner-file-import/learner-import-row-builder";
import { downloadLearnerImportTemplate } from "@/features/learner/components/learner-file-import/learner-import-template";
import { ImportLearnerFileConfirmModal } from "@/features/learner/components/learner-file-import/ImportLearnerFileConfirmModal";

// エラー一覧に出す上限。全件出すとモーダルが読みづらいため、残り件数のみ知らせる。
const MAX_VISIBLE_ERRORS = 20;

type Props = {
  fileImportModal: UseVisibleReturn;
};

export const ImportLearnerFileModal: FC<Props> = ({ fileImportModal }) => {
  const navigate = useNavigate();
  const [file, setFile] = useState<File | null>(null);
  const [errorList, setErrorList] = useState<string[]>([]);
  // ファイル解析中 (読み込み + マスタ突合)。処理中は入力欄を隠してスピナーを出す。
  const [isProcessing, setIsProcessing] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  // 解析済みの行。null 以外なら「取り込む外国人を選択」ステップを表示する (visa 一括同期と同じ選択手順)。
  const [parsedRows, setParsedRows] = useState<TBulkImportRow[] | null>(null);

  // 企業アカウントは所属機関を持たない (一括登録画面でも列を出さない) ため所属機関の突合をしない。
  const { userType } = useInitSetup();
  const isCompanyUser = userType === USER_TYPE.COMPANY_USER;

  // 突合・テンプレートのドロップダウンに使うマスタ一覧。
  const { data: companyListData, isLoading: isLoadingCompanies } = useGetListCompanyByRso(!isCompanyUser);
  const companyOptions = useMemo(
    () => (companyListData?.data ?? []).filter((c) => !c.isArchive).map((c) => ({ value: c.id, label: c.companyName })),
    [companyListData],
  );
  const { nationalityOptions: allNationalityOptions, isLoading: isLoadingNationalities } = useGetNationalityList();
  // 非表示の国籍はテンプレートのドロップダウンにも出さず、突合対象にもしない (選べない値を通してしまわないため)。
  const nationalityOptions = useMemo(() => allNationalityOptions.filter((opt) => !opt.hidden), [allNationalityOptions]);
  // 取り扱う分野が ON の分野のみ。テンプレートのドロップダウンにも突合にも同じ一覧を使う
  // (OFF の分野は取り扱わない方針のため、名称が一致しても取り込まない)。
  const { industryOptions, isLoading: isLoadingIndustries } = useLearnerIndustryOptions();
  const { data: japaneseLevelOptions, isLoading: isLoadingJapaneseLevels } = useEnum("EJapaneseLevelType");
  const { data: genderOptions, isLoading: isLoadingGenders } = useEnum("EGender");
  const { data: statusOptions, isLoading: isLoadingStatuses } = useEnum("EPlanStatus");
  // マスタ未取得のまま突合すると全行が「マスタに存在しません」になるため、取得完了までボタンを止める。
  const isLoadingMasters =
    isLoadingCompanies || isLoadingNationalities || isLoadingIndustries || isLoadingJapaneseLevels || isLoadingGenders || isLoadingStatuses;

  const onClose = () => {
    setFile(null);
    setErrorList([]);
    setIsProcessing(false);
    setParsedRows(null);
    fileImportModal.close();
  };

  const handleChangeFiles = (value: TExtendFile | TExtendFile[] | null) => {
    const files = [value ?? []].flat();
    setFile(files[0] ?? null);
    setErrorList([]);
  };

  const handleDownloadTemplate = async () => {
    try {
      setIsDownloading(true);
      await downloadLearnerImportTemplate({
        isCompanyUser,
        companyNames: companyOptions.map((opt) => opt.label),
        nationalityNames: nationalityOptions.map((opt) => opt.label),
        industryNames: industryOptions.map((opt) => opt.label),
        japaneseLevelNames: japaneseLevelOptions.map((opt) => opt.label),
        genderNames: genderOptions.map((opt) => opt.label),
        statusNames: statusOptions.map((opt) => opt.label),
      });
    } catch (err) {
      // 生成・保存に失敗したら黙って終わらせず通知する (押しても何も起きない状態を避ける)。
      showError(err);
    } finally {
      setIsDownloading(false);
    }
  };

  const handleImportFile = async () => {
    if (!file) return;
    setIsProcessing(true);
    setErrorList([]);
    try {
      const excelRows = await readLearnerImportFile(file, isCompanyUser);

      const { rows, errors } = buildImportRowsFromExcel(excelRows, {
        isCompanyUser,
        companyOptions,
        nationalityOptions,
        industryOptions,
        japaneseLevelOptions,
        genderOptions,
        statusOptions,
      });

      if (errors.length > 0) {
        const visible = errors.slice(0, MAX_VISIBLE_ERRORS);
        if (errors.length > MAX_VISIBLE_ERRORS) visible.push(`ほか${errors.length - MAX_VISIBLE_ERRORS}件のエラーがあります。`);
        setErrorList(visible);
        return;
      }
      if (rows.length === 0) {
        setErrorList(["取り込むデータがありません。入力済みの行があるかご確認ください。"]);
        return;
      }
      if (rows.length > MAX_IMPORT_LEARNERS) {
        setErrorList([`一度に取り込める外国人は${MAX_IMPORT_LEARNERS}名までです。${MAX_IMPORT_LEARNERS}名以内に分けて取り込んでください。`]);
        return;
      }
      // ここでは遷移せず、取り込む対象を選ばせるステップへ進む (visa 一括同期と同じ手順)。
      setParsedRows(rows);
    } catch (err) {
      setErrorList([err instanceof Error ? err.message : "ファイルの取り込みに失敗しました。"]);
    } finally {
      setIsProcessing(false);
    }
  };

  // 「CSV再取り込み」: 解析結果と選択中のファイルを破棄し、空の状態でファイル選択ステップへ戻す。
  const handleReimport = () => {
    setParsedRows(null);
    setFile(null);
    setErrorList([]);
  };

  // 選択ステップで確定した行のみを一括登録画面へ渡す。
  // 以降 (編集・アカウント発行・保存) は visa 一括同期と同じ画面に任せる。
  const handleConfirmImport = (selectedRows: TBulkImportRow[]) => {
    setParsedRows(null);
    setFile(null);
    fileImportModal.close();
    navigate(AppRoutes.learnerBulkImport, { state: { rows: selectedRows, source: "file" } });
  };

  return (
    <>
      {/* ステップ1: ファイル選択。選択ステップへ進んでいる間は隠し、「CSV再取り込み」で戻ってくる。 */}
      <Modal isOpen={fileImportModal.visible && !parsedRows} title="外国人情報の一括取り込み" width={920} enableFooter={false} onClose={onClose}>
        <Stack gap={3} mt={2}>
          {isProcessing ? (
            <Stack alignItems="center" justifyContent="center" gap={1} height={180}>
              <CircularProgress />
              <Typography variant="body14Light">処理中</Typography>
            </Stack>
          ) : (
            <Stack gap={2}>
              <DropZone
                options={{ multiple: false, noClick: true }}
                onChange={handleChangeFiles}
                rules={{
                  maxFile: 1,
                  acceptMimeTypes: [
                    "text/csv",
                    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                    "application/vnd.ms-excel.sheet.macroEnabled.12",
                  ],
                }}
                isShowErr={false}
                // noClick のため、ボタンから DropZone 内部の file dialog を開く。
                childrenRenderHtml={({ open }) => (
                  <>
                    <Typography variant="body14Light" textAlign="center">
                      アップロードするファイル（csv、xlsx、xlsm）をドラッグ&ドロップ
                    </Typography>
                    <Typography variant="body14Light" textAlign="center">
                      または
                    </Typography>
                    <Button type="button" variant="outline" onClick={open}>
                      ファイルを選択する
                    </Button>
                  </>
                )}
              />

              <Typography variant="cap12Light" color={text.secondary}>
                テンプレートの列名・列順のままご利用ください。1回の取り込みは{MAX_IMPORT_LEARNERS}名までです。
              </Typography>

              {errorList.length > 0 && (
                <Stack
                  gap={0.5}
                  borderRadius={rounded.xs}
                  sx={{ p: 1, background: background.error, color: text.error, maxHeight: 200, overflowY: "auto" }}
                >
                  {errorList.map((message) => (
                    <Typography key={message} component="p" variant="cap12">
                      {message}
                    </Typography>
                  ))}
                </Stack>
              )}
            </Stack>
          )}

          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Button variant="ghost" size="md" onClick={onClose}>
              <Typography variant="body14Semi">キャンセル</Typography>
            </Button>
            <Stack direction="row" gap={2}>
              <Button
                variant="outline"
                size="md"
                isLoading={isDownloading || isLoadingMasters}
                disabled={isDownloading || isLoadingMasters}
                onClick={handleDownloadTemplate}
                startIcon={<FileDownloadIcon />}
              >
                <Typography variant="body14Semi">テンプレートをダウンロード</Typography>
              </Button>
              <Button
                size="md"
                isLoading={isProcessing || isLoadingMasters}
                disabled={!file || isProcessing || isLoadingMasters}
                onClick={handleImportFile}
                startIcon={
                  <Box display="flex">
                    <IconImportFile width={16} height={19} color={text.white} />
                  </Box>
                }
              >
                <Typography variant="body14Semi">取り込み</Typography>
              </Button>
            </Stack>
          </Stack>
        </Stack>
      </Modal>

      {/* ステップ2: 取り込む外国人の選択。行が確定したときだけマウントし、「CSV再取り込み」で破棄してステップ1へ返す。 */}
      {parsedRows && (
        <ImportLearnerFileConfirmModal
          isOpen
          rows={parsedRows}
          isCompanyUser={isCompanyUser}
          japaneseLevelOptions={japaneseLevelOptions}
          onReimport={handleReimport}
          onCancel={onClose}
          onImport={handleConfirmImport}
        />
      )}
    </>
  );
};
